import ApiService from './apiService';
import {
    saveMicrophoneRecording,
    getMicrophoneRecording,
    removeMicrophoneRecording,
    updateMicrophoneRecordingProgress,
    getPendingMicrophoneRecordings,
    upsertPendingMicrophoneRecording,
    markMicrophoneRecordingPendingUpload,
    cleanupExpiredMicrophoneRecordings,
    pruneOrphanedPendingRecordings,
    StoredMicrophoneRecordingMeta
} from '../utils/recorderRecovery';
import {
    savePendingFileUpload,
    getPendingFileUpload,
    getPendingFileUploads,
    updatePendingFileUploadProgress,
    removePendingFileUpload,
    cleanupExpiredFileUploads,
    StoredPendingFileUploadMeta
} from '../utils/uploadRecovery';
import { getRecorderFileExtension } from '../utils/recorderBlob';
import { dispatchRecorderComplete } from '../utils/recorderEvents';
import { assertValidFileId, debugFileId } from '../utils/fileIdDebug';
import { logger } from '../utils/logger';

type UploadType = 'microphone' | 'upload';

export interface UploadServiceState {
    isUploading: boolean;
    progress: number;
    statusMessage: string;
    error: string | null;
    uploadId: string | null;
    uploadType: UploadType | null;
    isComplete: boolean;
    pendingMicrophoneRecording: StoredMicrophoneRecordingMeta | null;
    pendingFileUpload: StoredPendingFileUploadMeta | null;
}

interface UploadMicrophoneOptions {
    blob: Blob;
    recordingStartTime?: string | null;
    preferredFileId?: string;
    onComplete?: (fileId: string) => void;
}

interface ResumeMicrophoneOptions {
    onComplete?: (fileId: string) => void;
}

interface ResumeFileOptions {
    onComplete?: (fileId: string) => void;
}

interface UploadFileOptions {
    file: File;
    onComplete?: (fileId: string) => void;
}

interface UploadBlobOptions {
    blob: Blob;
    fileId: string;
    fileName: string;
    chunkSize: number;
    fileSizeBytes?: number;
    recordingStartedAt?: string | null;
    startChunk?: number;
    totalChunks?: number;
    messagePrefix?: string;
    networkPauseMessage: string;
    onChunkUploaded?: (uploadedChunks: number, totalChunks: number) => Promise<void> | void;
}

const DEFAULT_CHUNK_SIZE = 1024 * 1024; // 1MB
const UPLOAD_RESET_DELAY = 800;
const MAX_UPLOAD_RETRIES = 5;

type UploadListener = (state: UploadServiceState) => void;

class UploadService {
    private static instance: UploadService | null = null;

    private apiService: ApiService;
    private listeners: Set<UploadListener>;
    private state: UploadServiceState;
    private resetTimeout: ReturnType<typeof setTimeout> | null;

    private constructor() {
        this.apiService = new ApiService();
        this.listeners = new Set();
        this.resetTimeout = null;
        this.state = {
            isUploading: false,
            progress: 0,
            statusMessage: '',
            error: null,
            uploadId: null,
            uploadType: null,
            isComplete: false,
            pendingMicrophoneRecording: null,
            pendingFileUpload: null
        };

        void this.initializeRecoveryState();
    }

    private async initializeRecoveryState(): Promise<void> {
        try {
            await Promise.all([
                cleanupExpiredMicrophoneRecordings(),
                cleanupExpiredFileUploads()
            ]);
            // Ruim wees-opnames op (pending-vermelding zonder herstelbare audio)
            // voordat we de herstelstaat tonen, zodat er geen onuitvoerbare
            // herstelmelding verschijnt.
            await pruneOrphanedPendingRecordings();
        } catch (error) {
            logger.warn('Opschonen van verlopen recovery-data mislukt:', error);
        }

        await this.refreshPendingMicrophoneRecording();
        await this.refreshPendingFileUpload();
    }

    static getInstance(): UploadService {
        if (!UploadService.instance) {
            UploadService.instance = new UploadService();
        }
        return UploadService.instance;
    }

    getState(): UploadServiceState {
        return this.state;
    }

    async refreshRecoveryState(): Promise<void> {
        await this.refreshPendingMicrophoneRecording();
        await this.refreshPendingFileUpload();
    }

    // Wist een actieve foutmelding zonder de overige upload-/herstelstaat te raken,
    // zodat de gebruiker de melding kan wegklikken.
    clearError(): void {
        if (this.state.error === null) {
            return;
        }
        this.setState({ error: null });
    }

    subscribe(listener: UploadListener): () => void {
        this.listeners.add(listener);
        listener(this.state);
        return () => {
            this.listeners.delete(listener);
        };
    }

    async uploadMicrophoneRecording(options: UploadMicrophoneOptions): Promise<string | null> {
        const { blob, recordingStartTime = null, preferredFileId, onComplete } = options;
        if (!blob || blob.size === 0) {
            this.setState({
                error: 'Geen audio data gevonden voor upload.'
            });
            return null;
        }

        this.clearResetTimeout();

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        // Extensie volgt het opnameformaat: Safari neemt op in MP4/AAC, de rest
        // in WebM/Opus.
        const fileName = `notizy-microphone-${timestamp}.${getRecorderFileExtension(blob.type)}`;
        const fileId = preferredFileId || this.generateFileId();
        try {
            assertValidFileId(
                fileId,
                preferredFileId
                    ? 'src/services/uploadService.ts uploadMicrophoneRecording preferredFileId'
                    : 'src/services/uploadService.ts uploadMicrophoneRecording generated fileId'
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Ongeldige fileId';
            this.setState({
                error: message
            });
            return null;
        }
        const totalChunks = Math.ceil(blob.size / DEFAULT_CHUNK_SIZE);

        const pendingMeta: StoredMicrophoneRecordingMeta = {
            id: fileId,
            fileName,
            mimeType: blob.type || undefined,
            createdAt: Date.now(),
            recordingStartTime,
            uploadedChunks: 0,
            totalChunks,
            status: 'pending_upload'
        };

        let recoveryPersisted = false;

        // If this recording already exists in recovery storage, reuse it and only sync pending metadata.
        // De bewaarde chunks gelden alleen als geldige herstelkopie wanneer ze samen
        // exact de upload-blob vormen; anders schrijven we alsnog een volledige kopie.
        if (preferredFileId) {
            const existing = await getMicrophoneRecording(preferredFileId);
            if (existing?.blob && existing.blob.size === blob.size) {
                await markMicrophoneRecordingPendingUpload(preferredFileId);
                await upsertPendingMicrophoneRecording({
                    ...pendingMeta,
                    fileName: existing.fileName || pendingMeta.fileName,
                    recordingStartTime: recordingStartTime ?? existing.recordingStartTime ?? null
                });
                recoveryPersisted = true;
            }
        }

        if (!recoveryPersisted) {
            try {
                await saveMicrophoneRecording(fileId, blob, pendingMeta);
                recoveryPersisted = true;
            } catch (error) {
                logger.warn('Kon opname niet lokaal opslaan voor herstel, upload gaat wel door:', error);
            }
        }

        this.setState({
            isUploading: true,
            progress: 0,
            statusMessage: recoveryPersisted
                ? 'Upload van microfoon opname gestart...'
                : 'Upload gestart...',
            error: null,
            uploadId: fileId,
            uploadType: 'microphone',
            isComplete: false,
            pendingMicrophoneRecording: recoveryPersisted ? pendingMeta : null
        });

        try {
            const serverFileId = await this.uploadBlob({
                blob,
                fileId,
                fileName,
                chunkSize: DEFAULT_CHUNK_SIZE,
                fileSizeBytes: blob.size,
                recordingStartedAt: recordingStartTime,
                networkPauseMessage: 'Netwerk niet beschikbaar. Upload wordt hervat zodra de verbinding terug is.',
                onChunkUploaded: async (uploadedChunks, totalChunksValue) => {
                    await updateMicrophoneRecordingProgress(fileId, uploadedChunks, totalChunksValue);
                    this.updatePendingMicrophoneProgress(fileId, uploadedChunks, totalChunksValue);
                }
            });

            await removeMicrophoneRecording(fileId);
            await this.refreshPendingMicrophoneRecording();

            this.setState({
                isUploading: false,
                progress: 100,
                statusMessage: 'Upload voltooid!',
                isComplete: true,
                uploadId: fileId
            });

            // Naar buiten geven we het echte server-id (FileProgress.id) terug,
            // dat o.a. nodig is om notities als ondersteunend document te koppelen.
            // De lokale fileId blijft de sleutel voor herstel/opruiming hierboven.
            const completedFileId = serverFileId ?? fileId;
            onComplete?.(completedFileId);
            dispatchRecorderComplete(completedFileId);
            this.scheduleReset();
            return completedFileId;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Upload fout opgetreden';
            this.setState({
                isUploading: false,
                statusMessage: recoveryPersisted
                    ? 'Upload onderbroken. Je kunt deze later hervatten via de Recorder.'
                    : 'Upload mislukt. Opname kon niet lokaal worden gebufferd voor hervatten.',
                error: message,
                isComplete: false
            });
            return null;
        }
    }

    async resumePendingMicrophoneUpload(options: ResumeMicrophoneOptions = {}): Promise<boolean> {
        const { onComplete } = options;
        const pending = this.state.pendingMicrophoneRecording;
        if (!pending) {
            return false;
        }

        const stored = await getMicrophoneRecording(pending.id);
        if (!stored) {
            await removeMicrophoneRecording(pending.id).catch(() => undefined);
            await this.refreshPendingMicrophoneRecording();
            this.setState({
                statusMessage: this.getPendingRecoveryStatusMessage(),
                error: 'Kan opgeslagen opname niet vinden voor herstel.'
            });
            return false;
        }

        this.clearResetTimeout();

        const startChunk = stored.uploadedChunks || 0;
        const totalChunks = stored.totalChunks || Math.ceil(stored.blob.size / DEFAULT_CHUNK_SIZE);
        try {
            assertValidFileId(
                stored.id,
                'src/services/uploadService.ts resumePendingMicrophoneUpload stored.id from IndexedDB/localStorage'
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Ongeldige fileId';
            this.setState({
                isUploading: false,
                statusMessage: this.getPendingRecoveryStatusMessage(),
                error: message,
                isComplete: false
            });
            return false;
        }

        const initialProgress = Math.round((startChunk / totalChunks) * 100);

        this.setState({
            isUploading: true,
            uploadId: stored.id,
            uploadType: 'microphone',
            statusMessage: 'Hervatten van microfoon upload...',
            error: null,
            isComplete: false,
            progress: initialProgress
        });

        try {
            const serverFileId = await this.uploadBlob({
                blob: stored.blob,
                fileId: stored.id,
                fileName: stored.fileName,
                chunkSize: DEFAULT_CHUNK_SIZE,
                fileSizeBytes: stored.blob.size,
                recordingStartedAt: stored.recordingStartTime ?? null,
                startChunk,
                totalChunks,
                networkPauseMessage: 'Netwerk niet beschikbaar. Upload wordt hervat zodra de verbinding terug is.',
                onChunkUploaded: async (uploadedChunks, totalChunksValue) => {
                    await updateMicrophoneRecordingProgress(stored.id, uploadedChunks, totalChunksValue);
                    this.updatePendingMicrophoneProgress(stored.id, uploadedChunks, totalChunksValue);
                }
            });

            await removeMicrophoneRecording(stored.id);
            await this.refreshPendingMicrophoneRecording();

            this.setState({
                isUploading: false,
                progress: 100,
                statusMessage: 'Upload voltooid!',
                isComplete: true,
                uploadId: stored.id
            });

            const completedFileId = serverFileId ?? stored.id;
            onComplete?.(completedFileId);
            dispatchRecorderComplete(completedFileId);
            this.scheduleReset();
            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Hervatten mislukt';
            this.setState({
                isUploading: false,
                statusMessage: 'Upload blijft in wachtrij. Probeer opnieuw wanneer de verbinding stabiel is.',
                error: message,
                isComplete: false
            });
            return false;
        }
    }

    async discardPendingMicrophoneRecording(): Promise<void> {
        const pending = this.state.pendingMicrophoneRecording;
        if (!pending) {
            return;
        }

        await removeMicrophoneRecording(pending.id).catch(() => undefined);
        await this.refreshPendingMicrophoneRecording();

        this.setState({
            statusMessage: this.getPendingRecoveryStatusMessage(),
            error: null,
            progress: 0,
            isComplete: false,
            uploadId: null
        });
    }

    async resumePendingFileUpload(options: ResumeFileOptions = {}): Promise<boolean> {
        const { onComplete } = options;
        const pending = this.state.pendingFileUpload;
        if (!pending) {
            return false;
        }

        const stored = await getPendingFileUpload(pending.id);
        if (!stored) {
            await removePendingFileUpload(pending.id).catch(() => undefined);
            await this.refreshPendingFileUpload();
            this.setState({
                statusMessage: this.getPendingRecoveryStatusMessage(),
                error: 'Kan opgeslagen bestand niet vinden voor herstel.'
            });
            return false;
        }

        this.clearResetTimeout();

        const startChunk = stored.uploadedChunks || 0;
        const totalChunks = stored.totalChunks || Math.ceil(stored.blob.size / DEFAULT_CHUNK_SIZE);

        try {
            assertValidFileId(
                stored.id,
                'src/services/uploadService.ts resumePendingFileUpload stored.id from IndexedDB'
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Ongeldige fileId';
            this.setState({
                isUploading: false,
                statusMessage: this.getPendingRecoveryStatusMessage(),
                error: message,
                isComplete: false
            });
            return false;
        }

        const initialProgress = Math.round((startChunk / totalChunks) * 100);

        this.setState({
            isUploading: true,
            uploadId: stored.id,
            uploadType: 'upload',
            statusMessage: 'Hervatten van bestandsupload...',
            error: null,
            isComplete: false,
            progress: initialProgress
        });

        try {
            const serverFileId = await this.uploadBlob({
                blob: stored.blob,
                fileId: stored.id,
                fileName: stored.fileName,
                chunkSize: DEFAULT_CHUNK_SIZE,
                fileSizeBytes: stored.fileSize || stored.blob.size,
                recordingStartedAt: new Date(stored.createdAt).toISOString(),
                startChunk,
                totalChunks,
                networkPauseMessage: 'Bestandsupload gepauzeerd - wacht op netwerkherstel...',
                messagePrefix: 'Upload bezig',
                onChunkUploaded: async (uploadedChunks, totalChunksValue) => {
                    await updatePendingFileUploadProgress(stored.id, uploadedChunks, totalChunksValue);
                    this.updatePendingFileProgress(stored.id, uploadedChunks, totalChunksValue);
                }
            });

            await removePendingFileUpload(stored.id).catch(() => undefined);
            await this.refreshPendingFileUpload();

            this.setState({
                isUploading: false,
                progress: 100,
                statusMessage: 'Upload voltooid!',
                isComplete: true,
                uploadId: stored.id
            });

            const completedFileId = serverFileId ?? stored.id;
            onComplete?.(completedFileId);
            dispatchRecorderComplete(completedFileId);
            this.scheduleReset();
            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Hervatten mislukt';
            this.setState({
                isUploading: false,
                statusMessage: 'Bestandsupload blijft in wachtrij. Probeer opnieuw wanneer de verbinding stabiel is.',
                error: message,
                isComplete: false
            });
            return false;
        }
    }

    async discardPendingFileUpload(): Promise<void> {
        const pending = this.state.pendingFileUpload;
        if (!pending) {
            return;
        }

        await removePendingFileUpload(pending.id).catch(() => undefined);
        await this.refreshPendingFileUpload();

        this.setState({
            statusMessage: this.getPendingRecoveryStatusMessage(),
            error: null,
            progress: 0,
            isComplete: false,
            uploadId: null
        });
    }

    async uploadFile(options: UploadFileOptions): Promise<string | null> {
        const { file, onComplete } = options;

        try {
            this.validateFile(file);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Ongeldig bestand';
            this.setState({ error: message });
            return null;
        }

        this.clearResetTimeout();

        const fileId = this.generateFileId();
        const totalChunks = Math.ceil(file.size / DEFAULT_CHUNK_SIZE);
        const uploadStartTime = new Date().toISOString();
        const pendingMeta: StoredPendingFileUploadMeta = {
            id: fileId,
            fileName: file.name,
            createdAt: Date.now(),
            uploadedChunks: 0,
            totalChunks,
            fileSize: file.size,
            mimeType: file.type,
            status: 'pending_upload'
        };
        let recoveryPersisted = false;

        try {
            await savePendingFileUpload(fileId, file, pendingMeta);
            recoveryPersisted = true;
        } catch (error) {
            logger.warn('Kon bestand niet lokaal opslaan voor herstel, upload gaat wel door:', error);
        }

        this.setState({
            isUploading: true,
            progress: 0,
            statusMessage: recoveryPersisted
                ? 'Upload van bestand gestart...'
                : 'Upload gestart...',
            error: null,
            uploadId: fileId,
            uploadType: 'upload',
            isComplete: false,
            pendingFileUpload: recoveryPersisted ? pendingMeta : this.state.pendingFileUpload
        });

        try {
            const serverFileId = await this.uploadBlob({
                blob: file,
                fileId,
                fileName: file.name,
                chunkSize: DEFAULT_CHUNK_SIZE,
                fileSizeBytes: file.size,
                totalChunks,
                recordingStartedAt: uploadStartTime,
                networkPauseMessage: 'Bestandsupload gepauzeerd - wacht op netwerkherstel...',
                messagePrefix: 'Upload bezig',
                onChunkUploaded: async (uploadedChunks, totalChunksValue) => {
                    await updatePendingFileUploadProgress(fileId, uploadedChunks, totalChunksValue);
                    this.updatePendingFileProgress(fileId, uploadedChunks, totalChunksValue);
                }
            });

            await removePendingFileUpload(fileId).catch(() => undefined);
            await this.refreshPendingFileUpload();

            this.setState({
                isUploading: false,
                progress: 100,
                statusMessage: 'Upload voltooid!',
                isComplete: true,
                uploadId: fileId
            });

            const completedFileId = serverFileId ?? fileId;
            onComplete?.(completedFileId);
            dispatchRecorderComplete(completedFileId);
            this.scheduleReset();
            return completedFileId;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Upload fout opgetreden';
            this.setState({
                isUploading: false,
                statusMessage: recoveryPersisted
                    ? 'Upload onderbroken. Je kunt deze later hervatten via de Recorder.'
                    : 'Upload mislukt. Bestand kon niet lokaal worden gebufferd voor hervatten.',
                error: message,
                isComplete: false
            });
            return null;
        }
    }

    // Haalt het door de server toegekende (numerieke) file_progress_id uit een
    // chunk-respons. Dit is de echte opname-id (FileProgress.id) waarmee o.a. de
    // /supporting-documents/<int:file_id>-route werkt — niet de client-fileId
    // die we zelf genereren om chunks te groeperen.
    private static extractServerFileId(chunkResult: unknown): string | null {
        const raw = (chunkResult as { file_progress_id?: unknown })?.file_progress_id;
        if (raw === null || raw === undefined) {
            return null;
        }
        const normalized = String(raw).trim();
        return normalized ? normalized : null;
    }

    // Returnt het server-file_progress_id zodra de upload (laatste chunk) is
    // afgerond, of null als de server dat (nog) niet teruggaf.
    private async uploadBlob(options: UploadBlobOptions): Promise<string | null> {
        const {
            blob,
            fileId,
            fileName,
            chunkSize,
            fileSizeBytes = blob.size,
            recordingStartedAt = null,
            startChunk = 0,
            totalChunks: providedTotalChunks,
            messagePrefix = 'Upload bezig',
            networkPauseMessage,
            onChunkUploaded
        } = options;

        const totalChunks = providedTotalChunks ?? Math.ceil(blob.size / chunkSize);
        const oneSignalId = this.getOneSignalId();
        let serverFileId: string | null = null;

        if (totalChunks === 0 || startChunk >= totalChunks) {
            return null;
        }

        const initialProgress = Math.round((startChunk / totalChunks) * 100);
        if (startChunk > 0) {
            this.setState({
                progress: initialProgress
            });
        }

        // De server verwerkt chunks strikt in volgorde; uploaden gebeurt daarom
        // sequentieel en mag niet geparallelliseerd worden.
        for (let chunkIndex = startChunk; chunkIndex < totalChunks; chunkIndex += 1) {
            const start = chunkIndex * chunkSize;
            const end = Math.min(start + chunkSize, blob.size);
            const chunk = blob.slice(start, end);
            let retryCount = 0;

            // eslint-disable-next-line no-constant-condition
            while (true) {
                try {
                    const chunkResult = await this.apiService.uploadChunk(
                        chunk,
                        fileName,
                        chunkIndex,
                        totalChunks,
                        0,
                        1,
                        fileId,
                        'whisper',
                        oneSignalId,
                        recordingStartedAt ?? undefined,
                        fileSizeBytes,
                        chunkSize
                    );
                    const resolvedServerFileId = UploadService.extractServerFileId(chunkResult);
                    if (resolvedServerFileId) {
                        serverFileId = resolvedServerFileId;
                    }
                    break;
                } catch (error) {
                    if (typeof navigator !== 'undefined' && !navigator.onLine) {
                        this.setState({
                            statusMessage: networkPauseMessage
                        });
                        await UploadService.waitForOnline();
                        continue;
                    }

                    retryCount += 1;
                    if (!this.isRetriableUploadError(error) || retryCount >= MAX_UPLOAD_RETRIES) {
                        throw error instanceof Error ? error : new Error('Chunk upload mislukt');
                    }

                    const backoff = Math.min(15000, 1000 * Math.pow(2, retryCount - 1));
                    await UploadService.delay(backoff);
                }
            }

            const uploadedChunks = chunkIndex + 1;
            const progress = Math.round((uploadedChunks / totalChunks) * 100);
            const statusMessage = uploadedChunks === totalChunks
                ? 'Upload voltooid!'
                : `${messagePrefix}... ${progress}% voltooid`;

            this.setState({
                progress,
                statusMessage
            });

            if (onChunkUploaded) {
                await onChunkUploaded(uploadedChunks, totalChunks);
            }
        }

        return serverFileId;
    }

    private async refreshPendingMicrophoneRecording(): Promise<void> {
        try {
            const pending = await getPendingMicrophoneRecordings();
            const latest = pending.length > 0 ? pending[pending.length - 1] : null;
            this.setState({
                pendingMicrophoneRecording: latest,
                statusMessage: this.state.isUploading
                    ? this.state.statusMessage
                    : this.getPendingRecoveryStatusMessage(latest, this.state.pendingFileUpload)
            });
        } catch (error) {
            logger.warn('Kon pending opnames niet laden:', error);
        }
    }

    private updatePendingMicrophoneProgress(id: string, uploadedChunks: number, totalChunks: number): void {
        const current = this.state.pendingMicrophoneRecording;
        if (!current || current.id !== id) {
            return;
        }

        this.setState({
            pendingMicrophoneRecording: {
                ...current,
                uploadedChunks,
                totalChunks
            }
        });
    }

    private async refreshPendingFileUpload(): Promise<void> {
        try {
            const pending = await getPendingFileUploads();
            const latest = pending.length > 0 ? pending[pending.length - 1] : null;
            this.setState({
                pendingFileUpload: latest,
                statusMessage: this.state.isUploading
                    ? this.state.statusMessage
                    : this.getPendingRecoveryStatusMessage(this.state.pendingMicrophoneRecording, latest)
            });
        } catch (error) {
            logger.warn('Kon pending bestandsuploads niet laden:', error);
        }
    }

    private updatePendingFileProgress(id: string, uploadedChunks: number, totalChunks: number): void {
        const current = this.state.pendingFileUpload;
        if (!current || current.id !== id) {
            return;
        }

        this.setState({
            pendingFileUpload: {
                ...current,
                uploadedChunks,
                totalChunks,
                status: 'uploading'
            }
        });
    }

    private getPendingRecoveryStatusMessage(
        pendingMicrophoneRecording: StoredMicrophoneRecordingMeta | null = this.state.pendingMicrophoneRecording,
        pendingFileUpload: StoredPendingFileUploadMeta | null = this.state.pendingFileUpload
    ): string {
        if (pendingMicrophoneRecording) {
            return 'Niet-voltooide microfoon opname gevonden. Hervat de upload om gegevens te bewaren.';
        }

        if (pendingFileUpload) {
            return 'Niet-voltooide bestandsupload gevonden. Hervat de upload om gegevens te bewaren.';
        }

        return '';
    }

    private setState(update: Partial<UploadServiceState>): void {
        this.state = {
            ...this.state,
            ...update
        };
        this.notify();
    }

    private notify(): void {
        this.listeners.forEach(listener => {
            listener(this.state);
        });
    }

    private clearResetTimeout(): void {
        if (this.resetTimeout) {
            clearTimeout(this.resetTimeout);
            this.resetTimeout = null;
        }
    }

    private scheduleReset(delay = UPLOAD_RESET_DELAY): void {
        this.clearResetTimeout();
        this.resetTimeout = setTimeout(() => {
            this.resetTimeout = null;
            this.setState({
                progress: 0,
                statusMessage: this.getPendingRecoveryStatusMessage(),
                error: null,
                isComplete: false,
                uploadId: null,
                uploadType: null
            });
        }, delay);
    }

    private generateFileId(): string {
        const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        debugFileId('generated fileId', fileId);
        assertValidFileId(fileId, 'src/services/uploadService.ts generateFileId');
        return fileId;
    }

    private getOneSignalId(): string | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }
        try {
            return window.localStorage.getItem('oneSignalId') ?? undefined;
        } catch (error) {
            logger.warn('Kon oneSignalId niet lezen:', error);
            return undefined;
        }
    }

    private validateFile(file: File): void {
        const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/mp4', 'audio/x-m4a', 'audio/webm', 'audio/ogg', 'audio/aac'];
        const extensionRegex = /\.(mp3|wav|m4a|webm|ogg|aac)$/i;

        if (!validTypes.includes(file.type) && !extensionRegex.test(file.name)) {
            throw new Error('Ongeldig bestandstype. Ondersteunde formaten: MP3, WAV, M4A, WebM, OGG, AAC');
        }

        const maxSize = 200 * 1024 * 1024;
        if (file.size > maxSize) {
            throw new Error('Bestand te groot. Maximum grootte is 200MB.');
        }
    }

    private isRetriableUploadError(error: unknown): boolean {
        const uploadError = error as {
            code?: string;
            response?: {
                status?: number;
            };
        };

        const normalizedCode = uploadError?.code?.toUpperCase();
        if (normalizedCode === 'ECONNABORTED' || normalizedCode === 'ETIMEDOUT' || normalizedCode === 'ERR_NETWORK') {
            return true;
        }

        const status = uploadError?.response?.status;
        if (typeof status !== 'number') {
            return true;
        }

        return [408, 425, 429, 500, 502, 503, 504].includes(status);
    }

    private static async waitForOnline(): Promise<void> {
        if (typeof navigator === 'undefined' || navigator.onLine) {
            return;
        }

        await new Promise<void>((resolve) => {
            const handleOnline = () => {
                window.removeEventListener('online', handleOnline);
                resolve();
            };

            window.addEventListener('online', handleOnline);
        });
    }

    private static delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

export default UploadService;
